/**
 * Milestone status steps (D9, controls inventory §4.8): the page's primary is
 * the next step; every other step is one item in ⋯. Pure, unit-tested in
 * `src/utils/__tests__/milestoneStatusModel.test.ts`.
 */
import type { MilestoneStatus } from '../../types';
import { WORK_STATUS_TONE } from '../ui/tone';

export const MILESTONE_STEPS: readonly MilestoneStatus[] = ['planning', 'in_progress', 'in_review', 'testing', 'completed'];

/** Legacy values still in the data map onto the five steps (same mapping as the status labels). */
const LEGACY: Record<string, MilestoneStatus> = { pending: 'planning', open: 'in_progress', waiting: 'in_review', closed: 'completed' };

export function normalizeMilestoneStatus(status: string | null | undefined): MilestoneStatus {
  if (!status) return 'planning';
  if ((MILESTONE_STEPS as readonly string[]).includes(status)) return status as MilestoneStatus;
  return LEGACY[status] ?? 'planning';
}

/** The next step, or null when the milestone is completed. */
export function nextMilestoneStatus(status: string | null | undefined): MilestoneStatus | null {
  const i = MILESTONE_STEPS.indexOf(normalizeMilestoneStatus(status));
  return i >= 0 && i < MILESTONE_STEPS.length - 1 ? MILESTONE_STEPS[i + 1] : null;
}

/** Steps offered in ⋯: every step except the current one and the one the primary already offers. */
export function otherMilestoneSteps(status: string | null | undefined): MilestoneStatus[] {
  const current = normalizeMilestoneStatus(status);
  const next = nextMilestoneStatus(status);
  return MILESTONE_STEPS.filter((s) => s !== current && s !== next);
}

/** i18n key of the primary for moving to `next`. */
export function nextStepLabelKey(next: MilestoneStatus): string {
  if (next === 'in_progress') return 'milestonePage.startWork';
  if (next === 'completed') return 'milestonePage.complete';
  return 'milestonePage.moveTo';
}

/** D-56 — the progress a status stands for (the same map as the server's projectMetrics). */
export const STATUS_PROGRESS_PCT: Readonly<Record<MilestoneStatus, number>> = {
  planning: 0,
  in_progress: 25,
  in_review: 50,
  testing: 75,
  completed: 100,
};

/** P-53 - the options of the status pill (value + label + tone), in step order; `labelOf` gives the translated label. */
export function workStatusPillOptions(labelOf: (s: MilestoneStatus) => string): { value: MilestoneStatus; label: string; tone: (typeof WORK_STATUS_TONE)[MilestoneStatus] }[] {
  return MILESTONE_STEPS.map((s) => ({ value: s, label: labelOf(s), tone: WORK_STATUS_TONE[s] }));
}
